import {useRef, useCallback, useEffect} from 'react'

const bottomPadding = 120

/**
 * Hook that automatically follows the bottom of the
 * content. Useful for AI streaming content where
 * the length increases.
 */
export function useAutoScroll() {
  const autoScrollRef = useRef(true)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const onScrollRef = useRef<(() => void) | null>(null)
  const observerRef = useRef<ResizeObserver | null>(null)

  // Setup scroll event listener on window
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return

      const {scrollTop, scrollHeight, clientHeight} = document.documentElement
      const scrollTarget = scrollHeight - clientHeight

      // Consider the scroll position to be at the bottom if it's within the bottom
      // padding.
      autoScrollRef.current =
        Math.abs(scrollTop - scrollTarget) <= bottomPadding ||
        scrollTop >= scrollTarget
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const messageRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      containerRef.current = node

      const observer = new ResizeObserver(() => {
        if (autoScrollRef.current && containerRef.current) {
          const {scrollHeight, clientHeight} = containerRef.current
          const scrollTarget = scrollHeight - clientHeight

          containerRef.current.scrollTo({
            top: scrollTarget,
          })
        }
      })

      observer.observe(node)
      observerRef.current = observer
    } else {
      observerRef.current?.disconnect()
      observerRef.current = null
      containerRef.current = null
    }
  }, [])

  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      onScrollRef.current = () => {
        const {scrollTop, scrollHeight, clientHeight} = node
        const scrollTarget = scrollHeight - clientHeight

        autoScrollRef.current =
          Math.abs(scrollTop - scrollTarget) <= bottomPadding
      }

      node.addEventListener('scroll', onScrollRef.current)

      containerRef.current = node
    } else {
      if (onScrollRef.current) {
        containerRef.current?.removeEventListener('scroll', onScrollRef.current)
      }

      containerRef.current = null
      onScrollRef.current = null
    }
  }, [])
  return [messageRef, scrollRef]
}
